import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
} from "reactstrap";
import { format, parseISO, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import FeatherIcon from "feather-icons-react";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Widgets from "./Widgets";
import { toast } from "react-toastify";
import { APIClient } from "../../../helpers/api_helper";
import AwesomePagination from "../../../Components/Common/AwesomePagination";

const apiClient = new APIClient();

const CompraDiaria = () => {
  document.title = "Compra Diária | Painel Fort Exchange";

  // Estado para filtros e ordenação
  const [loading, setLoading] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [comprasDiarias, setComprasDiarias] = useState([]);

  const copyToClipboard = async (text) => {
    if (!navigator.clipboard) {
      toast.error("API de Clipboard não suportada no seu navegador.", {
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: false,
        className: "bg-danger text-white",
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para a área de transferência!", {
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: false,
        className: "bg-success text-white",
      });
    } catch (err) {
      toast.error("Falha ao copiar o texto: ", err, {
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: false,
        className: "bg-danger text-white",
      });
    }
  };

  function splitDateTime(dateTimeStr) {
    const dateTime = parseISO(dateTimeStr);

    const data = format(dateTime, "dd MMM, yyyy", { locale: ptBR });
    const hora = format(dateTime, "hh:mm a");

    return {
      data,
      hora,
    };
  }

  const formatCurrency = (value) => {
    if (!value) {
      return "";
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const fetchTransations = async () => {
    if (!loading) setLoading(true);
    const params = {
      itens_pagina: 20,
      pagina: pagina,
    };

    const response = await apiClient.post(
      `/cripto-td/compras-diarias/buscar`,
      params
    );

    if (response?.sucesso) {
      const dados = response.dados;
      setComprasDiarias(dados.data);
      setTotalPaginas(dados.last_page);
      setTotalItens(dados.total);
    } else {
      toast.error(response.data?.mensagem || "Erro ao buscar transações");
    }
    setLoading(false);
  };

  const handleButtonClick = async () => {
    setLoading(true);
    const response = await apiClient.post(`/cripto-td/compra-diaria`);
    if (response.sucesso) {
      toast.success(response.mensagem);
      await fetchTransations();
    } else {
      toast.error(response.mensagem);
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchTransations();
    };
    fetchData();
  }, [pagina]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Compra Diária" pageTitle="DCA Diário" />
          <Row>
            <Widgets />
          </Row>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">Últimas Compras Diárias</h5>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-success"
                      onClick={() => handleButtonClick()}
                      disabled={loading}
                    >
                      <i className="ri-bit-coin-fill align-bottom me-1"></i>
                      Nova Compra
                    </button>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Spinner height={100} width={100} />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Moeda</th>
                          <th>Data</th>
                          <th>Preço</th>
                          <th>Quantidade</th>
                          <th>Lucro</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comprasDiarias.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              Nenhuma compra encontrada.
                            </td>
                          </tr>
                        ) : (
                          comprasDiarias.map((item, idx) => (
                            <tr key={item.id || idx}>
                              {/* Moeda */}
                              <td>
                                <div className="d-flex align-items-center">
                                  <img
                                    src={`https://api.fort.exchange/crypto-icons/${item.moeda.toLowerCase()}.svg`}
                                    alt=""
                                    className="avatar-xxs me-2"
                                  />
                                  {item.moeda}
                                </div>
                              </td>
                              {/* Data */}
                              <td>
                                {splitDateTime(item.created_at).data}{" "}
                                <small className="text-muted">
                                  {splitDateTime(item.created_at).hora}
                                </small>
                              </td>
                              {/* Preço */}
                              <td>{formatCurrency(item.cotacao_brl)}</td>
                              {/* Quantidade */}
                              <td>
                                <h6
                                  className={"text-success amount mb-1"}
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    copyToClipboard(item.quantidade_moeda)
                                  }
                                  title="Clique para copiar o valor"
                                >
                                  {parseFloat(item.quantidade_moeda).toFixed(4)}{" "}
                                  {item.moeda}{" "}
                                  <FeatherIcon
                                    icon="copy"
                                    style={{ color: "#ccc", height: "15px" }}
                                  />
                                </h6>
                                <p className="text-muted mb-0">
                                  {formatCurrency(item.quantidade_brl)}
                                </p>
                              </td>
                              {/* Lucro */}
                              <td>{formatCurrency(item.lucro)}</td>
                              {/* Ações */}
                              <td>
                                {(() => {
                                  const moeda = item.moeda;
                                  const data_max_raw = new Date(
                                    item.created_at
                                  );
                                  const data_max = format(
                                    new Date(
                                      data_max_raw.setDate(
                                        data_max_raw.getDate() + 1
                                      )
                                    ),
                                    "yyyy-MM-dd"
                                  );
                                  const data_min = format(
                                    subDays(new Date(data_max), 7),
                                    "yyyy-MM-dd"
                                  );
                                  return (
                                    <Link
                                      to={`/compra-diaria-transacoes?moeda=${moeda}&data_min=${data_min}&data_max=${data_max}`}
                                      className="btn btn-sm btn-primary"
                                    >
                                      Ver Transações
                                    </Link>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Paginação */}
                  <AwesomePagination pagina={pagina} totalPaginas={totalPaginas} setPagina={setPagina} />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CompraDiaria;
